import React from 'react';
import { ErrorFallback } from './ErrorFallback';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorCount: number;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  resetOnPropsChange?: boolean;
}

/**
 * Enhanced Error Boundary Component
 *
 * Features:
 * - Catches rendering errors in child components
 * - Provides fallback UI with retry functionality
 * - Reports errors to logging service in production
 * - Tracks error count to prevent infinite error loops
 * - Supports custom fallback components
 *
 * @example
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private errorReportTimeout?: NodeJS.Timeout;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update state with error details
    this.setState((prevState) => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Log error to console
    console.error('Error Boundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Report to error tracking service in production
    this.reportErrorToService(error, errorInfo);

    // Prevent infinite error loops
    if (this.state.errorCount >= 3) {
      console.error(
        'Error Boundary: Too many errors caught. Please refresh the page.'
      );
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state when props change (if enabled)
    if (
      this.props.resetOnPropsChange &&
      prevProps.children !== this.props.children &&
      this.state.hasError
    ) {
      this.resetError();
    }
  }

  componentWillUnmount() {
    // Clear any pending error report timeouts
    if (this.errorReportTimeout) {
      clearTimeout(this.errorReportTimeout);
    }
  }

  /**
   * Report error to external logging service
   */
  private reportErrorToService(error: Error, errorInfo: React.ErrorInfo): void {
    // Debounce error reporting to avoid spam
    if (this.errorReportTimeout) {
      clearTimeout(this.errorReportTimeout);
    }

    this.errorReportTimeout = setTimeout(() => {
      if (process.env.NODE_ENV === 'production') {
        try {
          // Report to PostHog
          if (typeof window !== 'undefined' && (window as unknown as { posthog?: { capture: (event: string, data: unknown) => void } }).posthog) {
            (window as unknown as { posthog: { capture: (event: string, data: unknown) => void } }).posthog.capture('error_boundary', {
              error: {
                name: error.name,
                message: error.message,
                stack: error.stack,
              },
              errorInfo: {
                componentStack: errorInfo.componentStack,
              },
              url: window.location.href,
              userAgent: navigator.userAgent,
            });
          }

          // You can add other error tracking services here
          // Example: Sentry
          // if (typeof window !== 'undefined' && (window as any).Sentry) {
          //   (window as any).Sentry.captureException(error, {
          //     contexts: {
          //       react: {
          //         componentStack: errorInfo.componentStack,
          //       },
          //     },
          //   });
          // }

          // Send to custom error logging endpoint
          fetch('/api/log-error', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              error: {
                name: error.name,
                message: error.message,
                stack: error.stack,
              },
              errorInfo: {
                componentStack: errorInfo.componentStack,
              },
              timestamp: new Date().toISOString(),
              url: window.location.href,
            }),
          }).catch((fetchError) => {
            console.error('Failed to report error:', fetchError);
          });
        } catch (reportError) {
          console.error('Error reporting failed:', reportError);
        }
      }
    }, 1000);
  }

  /**
   * Reset error state and re-render children
   */
  resetError = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    });
  };

  render() {
    const { hasError, error, errorCount } = this.state;

    if (hasError) {
      // Prevent rendering if too many errors (infinite loop protection)
      if (errorCount >= 3) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="max-w-md w-full text-center">
              <div className="mb-4 p-4 bg-destructive/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <svg
                  className="h-8 w-8 text-destructive"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-destructive mb-2">
                Critical Error
              </h1>
              <p className="text-muted-foreground mb-6">
                The application encountered multiple errors. Please refresh the
                page to continue.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
              >
                Refresh Page
              </button>
            </div>
          </div>
        );
      }

      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={error} resetError={this.resetError} />;
      }

      // Use default fallback
      return <ErrorFallback error={error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}
